/**
 * Candidate Detail stage-tab + evaluation deeplink helpers.
 * Keeps URL ↔ tab selection deterministic (no double-click race).
 */

/**
 * Resolve which stage tab to show from a deeplink `tab` query value.
 * @returns {string|null} Tab value to apply, or null when caller should wait / leave state alone.
 */
export function resolveCandidateDetailTab({
  tabFromUrl = null,
  availableTabValues = new Set(),
  tabsReady = false,
} = {}) {
  if (!tabFromUrl) return null;
  if (availableTabValues instanceof Set
    ? availableTabValues.has(tabFromUrl)
    : availableTabValues.includes?.(tabFromUrl)) {
    return tabFromUrl;
  }
  // Pipeline tabs not ready yet — wait. Once ready, fall back if still invalid.
  if (tabsReady) return 'resume';
  return null;
}

/**
 * Legacy panel notifs used `tab=panels`. Map to `panel-{pipeline_stage_id}` when possible.
 */
export function resolvePanelTabFromDeeplink({
  tabFromUrl = null,
  panelId = null,
  panels = [],
} = {}) {
  if (!panelId) return tabFromUrl;
  const panel = (panels || []).find((p) => p && p.id === panelId);
  const stageId = panel?.pipeline_stage_id;
  if (!stageId) return tabFromUrl;
  const panelTab = `panel-${stageId}`;
  if (!tabFromUrl || tabFromUrl === 'panels') return panelTab;
  return tabFromUrl;
}

/**
 * Build next search params after a user stage-tab click (URL is SSOT for `tab`).
 */
export function nextTabSearchParams(searchParams, tabValue) {
  const next = new URLSearchParams(searchParams);
  if (tabValue) next.set('tab', String(tabValue));
  else next.delete('tab');
  return next;
}

/**
 * Pick evaluation id for Candidate Detail pills / stage APIs.
 * Explicit pill selection wins; then URL `eval` / panel ownership; then newest.
 * Keep `eval` in the URL so Strict Mode remount (selected=null) still hits URL.
 */
export function resolveEvaluationSelection({
  evalFromUrl = null,
  evaluationIds = [],
  panelEvaluationId = null,
  selectedEvaluationId = null,
  panelDeeplinkPending = false,
} = {}) {
  const ids = evaluationIds || [];
  if (selectedEvaluationId && ids.includes(selectedEvaluationId)) {
    return { evaluationId: selectedEvaluationId, source: 'selected' };
  }
  if (evalFromUrl && ids.includes(evalFromUrl)) {
    return { evaluationId: evalFromUrl, source: 'url' };
  }
  if (panelEvaluationId && ids.includes(panelEvaluationId)) {
    return { evaluationId: panelEvaluationId, source: 'panel' };
  }
  if (panelDeeplinkPending) {
    return { evaluationId: null, source: 'pending' };
  }
  if (ids[0]) {
    return { evaluationId: ids[0], source: 'default' };
  }
  return { evaluationId: null, source: 'none' };
}
